/**
 * The attribute names `r-list` reads.
 *
 * Every one is list-specific, including the links, which are conceptually the
 * same numbered list `r-text` already offers. Sharing that attribute name
 * would have saved one line of editor wiring and cost the documentation
 * contract: RuleCMS publishes one page per component-scope attribute and
 * requires exactly one owning component, so a shared name has no single page
 * to link to from the drawer. Reuse happens a level down instead — the
 * composer resolves an attribute to an *editor* by name, and `list-links`
 * points at the editor `links-3-resolutions` already resolves to.
 *
 * Nothing here duplicates a column style attribute. Spacing, colour and
 * typography come from Column Styles on the root element and inherit into
 * the items; the attributes below are only what is specific to a list —
 * which element it is, what the markers look like, and where numbering
 * starts. Kind and marker are closed sets rather than free text, which is
 * what lets them go through the composer's shared select and lets the render
 * path treat anything unrecognised as unset.
 */
export const LIST_ITEMS_ATTRIBUTE = 'list-items-3-resolutions';
export const LIST_KIND_ATTRIBUTE = 'list-kind-3-resolutions';
export const LIST_MARKER_ATTRIBUTE = 'list-marker-3-resolutions';
export const LIST_START_ATTRIBUTE = 'list-start-3-resolutions';
export const LIST_REVERSED_ATTRIBUTE = 'list-reversed-3-resolutions';
export const LIST_LINKS_ATTRIBUTE = 'list-links-3-resolutions';
export const LIST_INLINE_STYLES_ATTRIBUTE = 'list-inline-styles-3-resolutions';

/** What a freshly dropped list shows before the author types anything. */
export const DEFAULT_LIST_ITEMS = 'First item\nSecond item\nThird item';

export const LIST_KINDS = ['unordered', 'ordered'] as const;
export type ListKind = (typeof LIST_KINDS)[number];
export const DEFAULT_LIST_KIND: ListKind = 'unordered';

/**
 * `list-style-type` values worth offering. Every entry is valid CSS on both
 * `<ul>` and `<ol>`, so the marker survives the author switching kind rather
 * than resetting. Unset means the browser default — disc for unordered,
 * decimal for ordered — which is why there is no default constant here: not
 * setting the property at all is the default.
 */
export const LIST_MARKERS = [
  'disc',
  'circle',
  'square',
  'decimal',
  'lower-alpha',
  'upper-alpha',
  'lower-roman',
  'upper-roman',
  'none',
] as const;
export type ListMarker = (typeof LIST_MARKERS)[number];

type Attributes = { [key: string]: any } | undefined;

export const resolveListKind = (componentAttributes: Attributes): ListKind => {
  const value = componentAttributes?.[LIST_KIND_ATTRIBUTE];
  return LIST_KINDS.includes(value) ? value : DEFAULT_LIST_KIND;
};

/**
 * A stored value reaches a CSS property here, so the allowlist is the
 * boundary that keeps it from being whatever a request happened to write.
 * Unrecognised values mean "unset" and the browser default applies.
 */
export const resolveListMarker = (componentAttributes: Attributes): ListMarker | undefined => {
  const value = componentAttributes?.[LIST_MARKER_ATTRIBUTE];
  return LIST_MARKERS.includes(value) ? value : undefined;
};

/**
 * One item per line of the authored text, blank lines dropped. An unset
 * attribute falls back to the placeholder items, but text the author
 * deliberately cleared stays cleared — an empty list element still carries
 * the column's styles and stays selectable in the composer.
 */
export const resolveListItems = (componentAttributes: Attributes): string[] => {
  const value = componentAttributes?.[LIST_ITEMS_ATTRIBUTE];
  const text =
    value === undefined || value === null
      ? DEFAULT_LIST_ITEMS
      : typeof value === 'string'
        ? value
        : String(value);
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '');
};

/**
 * HTML's `start` takes any integer, including negative ones. The editor
 * stores a number, but a hand-written or migrated config may carry a numeric
 * string; both mean the same thing here. Anything else is unset, and only an
 * ordered list ever reads it.
 */
export const resolveListStart = (componentAttributes: Attributes): number | undefined => {
  const value = componentAttributes?.[LIST_START_ATTRIBUTE];
  if (typeof value === 'number' && Number.isInteger(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value.trim());
    if (Number.isInteger(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

/**
 * Select editors in the composer store `'true'` / `'false'` as strings, while
 * a hand-written or migrated config may carry a real boolean. Both mean the
 * same thing here. Only an ordered list ever reads it.
 */
export const resolveListReversed = (componentAttributes: Attributes): boolean => {
  const value = componentAttributes?.[LIST_REVERSED_ATTRIBUTE];
  if (typeof value === 'boolean') {
    return value;
  }
  return typeof value === 'string' && value.trim().toLowerCase() === 'true';
};
